// Multi-file drawOla store.
//
// Data model:
//  - documents: { id, name, createdAt, updatedAt, sizeBytes, elements, appState, fileIds[] }
//  - binaries:  { fileId, data: BinaryFileData, refCount }   (content-addressed)
//  - meta:      activeDocId, drawolaUsage, migrationVersion
//
// Writes are single-transaction across all three stores → no orphans on
// mid-write failure. Binary dedup via refCount keyed by Excalidraw's fileId
// (already a content hash).

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";

import { META_KEYS, STORES, promisify, txDone, openDb } from "./db";
import { emitDocWritten } from "./docEvents";
import { computeSceneKey } from "./sceneKey";
import { extractSearchStrings } from "./searchText";
import { estimateDocBytes } from "./usage";

import type {
  CloudBinding,
  Doc,
  DocCollabRoom,
  DocMeta,
  DocShare,
} from "./types";

type StoredDoc = DocMeta & {
  elements: readonly OrderedExcalidrawElement[];
  appState: Partial<AppState>;
  fileIds: string[];
};

type StoredBinary = {
  fileId: string;
  data: BinaryFileData;
  refCount: number;
};

function newId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `doc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function toDocMeta(d: StoredDoc): DocMeta {
  return {
    id: d.id,
    name: d.name,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    sizeBytes: d.sizeBytes,
    sceneKey: d.sceneKey,
    thumbnail: d.thumbnail,
    cloud: d.cloud,
    collabRoom: d.collabRoom,
    shares: d.shares,
  };
}

/**
 * A doc as the Studio list sees it: its metadata plus the strings its scene can
 * be searched by.
 *
 * `searchStrings` is derived, never stored: it is computed here from the
 * elements `listDocs()` already has in hand, and no write path puts it back
 * into IndexedDB (they all rebuild the stored object explicitly).
 */
export type DocListEntry = DocMeta & { searchStrings: string[] };

export async function listDocs(): Promise<DocListEntry[]> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readonly");
  const all = (await promisify(
    tx.objectStore(STORES.DOCS).getAll() as IDBRequest<StoredDoc[]>,
  )) as StoredDoc[];
  await txDone(tx);
  return all
    .map((d) => ({
      ...toDocMeta(d),
      searchStrings: extractSearchStrings(d.elements),
    }))
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * Metadata for a single document — name, timestamps, size, thumbnail.
 *
 * Cheaper than both alternatives for callers that only need the name (e.g. the
 * editor breadcrumb): `listDocs()` does a `getAll()` over every stored document
 * (elements included), and `getDoc()` additionally walks the `binaries` store.
 */
export async function getDocMeta(id: string): Promise<DocMeta | null> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readonly");
  const stored = (await promisify(
    tx.objectStore(STORES.DOCS).get(id) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  await txDone(tx);
  return stored ? toDocMeta(stored) : null;
}

export async function getDoc(id: string): Promise<Doc | null> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS, STORES.BINARIES], "readonly");
  const stored = (await promisify(
    tx.objectStore(STORES.DOCS).get(id) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!stored) {
    await txDone(tx);
    return null;
  }
  const files: BinaryFiles = {};
  for (const fid of stored.fileIds) {
    const bin = (await promisify(
      tx.objectStore(STORES.BINARIES).get(fid) as IDBRequest<
        StoredBinary | undefined
      >,
    )) as StoredBinary | undefined;
    if (bin) {
      files[fid] = bin.data;
    }
  }
  await txDone(tx);
  return {
    id: stored.id,
    name: stored.name,
    createdAt: stored.createdAt,
    updatedAt: stored.updatedAt,
    sizeBytes: stored.sizeBytes,
    sceneKey: stored.sceneKey,
    thumbnail: stored.thumbnail,
    cloud: stored.cloud,
    collabRoom: stored.collabRoom,
    shares: stored.shares,
    elements: stored.elements,
    appState: stored.appState,
    files,
  };
}

/**
 * Atomically save a document. Performs binary diff (refCount inc/dec) and GC
 * (delete binaries whose refCount hits 0) inside a single transaction.
 *
 * @param id     document id (create a new one via createDoc first)
 * @param update partial update (elements, appState, files, name)
 */
export async function saveDoc(
  id: string,
  update: {
    elements?: readonly OrderedExcalidrawElement[];
    appState?: Partial<AppState>;
    files?: BinaryFiles;
    name?: string;
    /** Pass `null` to clear the binding, omit to keep existing. */
    cloud?: CloudBinding | null;
  },
): Promise<DocMeta> {
  const db = await openDb();
  const tx = db.transaction(
    [STORES.DOCS, STORES.BINARIES, STORES.META],
    "readwrite",
  );
  const docsStore = tx.objectStore(STORES.DOCS);
  const binStore = tx.objectStore(STORES.BINARIES);
  const metaStore = tx.objectStore(STORES.META);

  const existing = (await promisify(
    docsStore.get(id) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!existing) {
    tx.abort();
    throw new Error(`Doc not found: ${id}`);
  }

  const nextElements = update.elements ?? existing.elements;
  const nextAppState = update.appState ?? existing.appState;
  const nextFiles = update.files;
  const nextName = update.name ?? existing.name;
  // A cloud-only write (the sync engine recording a binding) is not an edit:
  // the Studio sorts and labels by `updatedAt`, and the sync engine compares
  // `sceneKey` against the binding, so both must stay put.
  const touchesContent =
    update.elements !== undefined ||
    update.files !== undefined ||
    update.name !== undefined;
  const nextCloud =
    update.cloud === null ? undefined : update.cloud ?? existing.cloud;

  const oldFileIds = new Set(existing.fileIds);
  const newFileIds = new Set<string>(existing.fileIds);

  if (nextFiles) {
    // rebuild fileIds from the incoming files, keyed by Excalidraw fileId
    newFileIds.clear();
    for (const fid in nextFiles) {
      newFileIds.add(fid);
    }

    // added ids: bump refCount or insert
    for (const fid of newFileIds) {
      if (oldFileIds.has(fid)) {
        continue;
      }
      const data = nextFiles[fid];
      if (!data) {
        continue;
      }
      const prev = (await promisify(
        binStore.get(fid) as IDBRequest<StoredBinary | undefined>,
      )) as StoredBinary | undefined;
      if (prev) {
        prev.refCount += 1;
        await promisify(binStore.put(prev));
      } else {
        const nextBin: StoredBinary = { fileId: fid, data, refCount: 1 };
        await promisify(binStore.put(nextBin));
      }
    }

    // removed ids: decrement and GC
    for (const fid of oldFileIds) {
      if (newFileIds.has(fid)) {
        continue;
      }
      const prev = (await promisify(
        binStore.get(fid) as IDBRequest<StoredBinary | undefined>,
      )) as StoredBinary | undefined;
      if (!prev) {
        continue;
      }
      const nextRef = prev.refCount - 1;
      if (nextRef <= 0) {
        await promisify(binStore.delete(fid));
      } else {
        prev.refCount = nextRef;
        await promisify(binStore.put(prev));
      }
    }
  }

  const fileIdsArr = Array.from(newFileIds);
  const resolvedFiles: BinaryFiles = {};
  for (const fid of fileIdsArr) {
    if (nextFiles && nextFiles[fid]) {
      resolvedFiles[fid] = nextFiles[fid];
    } else {
      const prev = (await promisify(
        binStore.get(fid) as IDBRequest<StoredBinary | undefined>,
      )) as StoredBinary | undefined;
      if (prev) {
        resolvedFiles[fid] = prev.data;
      }
    }
  }
  const sizeBytes = estimateDocBytes(nextElements, nextAppState, resolvedFiles);

  const nextDoc: StoredDoc = {
    id,
    name: nextName,
    createdAt: existing.createdAt,
    updatedAt: touchesContent ? Date.now() : existing.updatedAt,
    sizeBytes,
    sceneKey: touchesContent
      ? computeSceneKey(nextElements, fileIdsArr, nextName)
      : existing.sceneKey,
    thumbnail: existing.thumbnail,
    cloud: nextCloud,
    // `saveDoc` rebuilds the stored object field by field, so anything not
    // named here is dropped on the first write. The collaboration marker has
    // to survive every save of the session's own scene.
    collabRoom: existing.collabRoom,
    shares: existing.shares,
    elements: nextElements,
    appState: nextAppState,
    fileIds: fileIdsArr,
  };
  await promisify(docsStore.put(nextDoc));

  // update drawolaUsage ledger
  const prevUsage =
    ((await promisify(
      metaStore.get(META_KEYS.USAGE) as IDBRequest<number | undefined>,
    )) as number | undefined) ?? 0;
  const newUsage = prevUsage - (existing.sizeBytes ?? 0) + sizeBytes;
  await promisify(metaStore.put(Math.max(0, newUsage), META_KEYS.USAGE));

  await txDone(tx);
  if (touchesContent) {
    emitDocWritten(id);
  }
  return toDocMeta(nextDoc);
}

export async function createDoc(
  name: string,
  initial?: {
    elements?: readonly OrderedExcalidrawElement[];
    appState?: Partial<AppState>;
    files?: BinaryFiles;
  },
): Promise<DocMeta> {
  const id = newId();
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS, STORES.META], "readwrite");
  const now = Date.now();
  const elements = initial?.elements ?? [];
  const appState = initial?.appState ?? {};
  const emptyFileIds: string[] = [];
  const sizeBytes = estimateDocBytes(elements, appState, {});
  // no shares: createDoc builds from scratch, a duplicate cannot inherit them
  const doc: StoredDoc = {
    id,
    name,
    createdAt: now,
    updatedAt: now,
    sizeBytes,
    sceneKey: computeSceneKey(elements, emptyFileIds, name),
    elements,
    appState,
    fileIds: emptyFileIds,
  };
  await promisify(tx.objectStore(STORES.DOCS).put(doc));
  const metaStore = tx.objectStore(STORES.META);
  const prevUsage =
    ((await promisify(
      metaStore.get(META_KEYS.USAGE) as IDBRequest<number | undefined>,
    )) as number | undefined) ?? 0;
  await promisify(metaStore.put(prevUsage + sizeBytes, META_KEYS.USAGE));
  await txDone(tx);
  const meta = toDocMeta(doc);
  // if files present, save them in a follow-up saveDoc call so we share
  // the refCount logic
  if (initial?.files && Object.keys(initial.files).length) {
    return saveDoc(id, { files: initial.files });
  }
  return meta;
}

/**
 * Write (or clear, passing `null`) a doc's Studio preview.
 *
 * Deliberately touches nothing else: not `elements`, not `updatedAt` (a
 * backfill must never reorder the Studio's "recently updated" list) and not
 * `sizeBytes` — see the SIZE LEDGER note in `./thumbnails.ts` for why the
 * preview stays out of the byte ledger.
 *
 * @returns `true` when the stored value actually changed.
 */
export async function setDocThumbnail(
  id: string,
  thumbnail: string | null,
): Promise<boolean> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readwrite");
  const docsStore = tx.objectStore(STORES.DOCS);
  const existing = (await promisify(
    docsStore.get(id) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!existing) {
    await txDone(tx);
    return false;
  }
  const next = thumbnail ?? undefined;
  if (existing.thumbnail === next) {
    await txDone(tx);
    return false;
  }
  if (next === undefined) {
    delete existing.thumbnail;
  } else {
    existing.thumbnail = next;
  }
  await promisify(docsStore.put(existing));
  await txDone(tx);
  return true;
}

/**
 * Append a shareable-link record to a doc.
 *
 * Deliberately touches nothing else: not `updatedAt` (creating a link is not
 * an edit to the drawing, and moving `updatedAt` would reorder the Studio's
 * "recently updated" list) and not `sizeBytes` (the share record is metadata,
 * not drawing bytes).
 *
 * No-op when the doc does not exist.
 */
export async function addDocShare(
  docId: string,
  share: DocShare,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readwrite");
  const docsStore = tx.objectStore(STORES.DOCS);
  const existing = (await promisify(
    docsStore.get(docId) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!existing) {
    await txDone(tx);
    return;
  }
  existing.shares = [...(existing.shares ?? []), share];
  await promisify(docsStore.put(existing));
  await txDone(tx);
}

/**
 * Remove a shareable-link record from a doc.
 *
 * Same "not an edit to the drawing" rule as `addDocShare`: `updatedAt` and
 * `sizeBytes` stay untouched.
 *
 * No-op when the doc does not exist or the share id is unknown.
 */
export async function removeDocShare(
  docId: string,
  shareId: string,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readwrite");
  const docsStore = tx.objectStore(STORES.DOCS);
  const existing = (await promisify(
    docsStore.get(docId) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!existing) {
    await txDone(tx);
    return;
  }
  const next = (existing.shares ?? []).filter((s) => s.id !== shareId);
  if (next.length === (existing.shares?.length ?? 0)) {
    await txDone(tx);
    return;
  }
  existing.shares = next;
  await promisify(docsStore.put(existing));
  await txDone(tx);
}

/**
 * Find the draw that belongs to a live collaboration room, if there is one.
 *
 * Used when entering a room, so a page reload back into the same room reuses
 * the draw it already had instead of leaving an orphan behind and making a
 * second one.
 *
 * Scans the `docs` store directly rather than going through `listDocs()`,
 * which also extracts every document's search strings.
 */
export async function findDocByCollabRoom(
  roomId: string,
): Promise<DocMeta | null> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readonly");
  const all = (await promisify(
    tx.objectStore(STORES.DOCS).getAll() as IDBRequest<StoredDoc[]>,
  )) as StoredDoc[];
  await txDone(tx);
  const match = all.find((d) => d.collabRoom?.roomId === roomId);
  return match ? toDocMeta(match) : null;
}

/**
 * Mark (or, with `null`, unmark) a draw as belonging to a collaboration room.
 *
 * Not an edit to the drawing, so `updatedAt` and `sizeBytes` stay untouched:
 * same rule as `addDocShare`. Writing the marker must not reorder the Studio's
 * "recently updated" list.
 *
 * Clearing is guarded by `roomId`: a session may only take back its own
 * marker, never one a different room wrote.
 *
 * No-op when the doc does not exist.
 */
export async function setDocCollabRoom(
  docId: string,
  collabRoom: DocCollabRoom | null,
  clearingRoomId?: string,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readwrite");
  const docsStore = tx.objectStore(STORES.DOCS);
  const existing = (await promisify(
    docsStore.get(docId) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!existing) {
    await txDone(tx);
    return;
  }
  if (
    collabRoom === null &&
    clearingRoomId !== undefined &&
    existing.collabRoom?.roomId !== clearingRoomId
  ) {
    await txDone(tx);
    return;
  }
  if (collabRoom === null) {
    delete existing.collabRoom;
  } else {
    existing.collabRoom = collabRoom;
  }
  await promisify(docsStore.put(existing));
  await txDone(tx);
}

export async function renameDoc(id: string, name: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readwrite");
  const docsStore = tx.objectStore(STORES.DOCS);
  const existing = (await promisify(
    docsStore.get(id) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!existing) {
    tx.abort();
    throw new Error(`Doc not found: ${id}`);
  }
  existing.name = name;
  existing.updatedAt = Date.now();
  existing.sceneKey = computeSceneKey(
    existing.elements,
    existing.fileIds,
    name,
  );
  await promisify(docsStore.put(existing));
  await txDone(tx);
  emitDocWritten(id);
}

export async function deleteDoc(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(
    [STORES.DOCS, STORES.BINARIES, STORES.META],
    "readwrite",
  );
  const docsStore = tx.objectStore(STORES.DOCS);
  const binStore = tx.objectStore(STORES.BINARIES);
  const metaStore = tx.objectStore(STORES.META);
  const existing = (await promisify(
    docsStore.get(id) as IDBRequest<StoredDoc | undefined>,
  )) as StoredDoc | undefined;
  if (!existing) {
    await txDone(tx);
    return;
  }
  // decrement refCount for all files, GC zeros
  for (const fid of existing.fileIds) {
    const prev = (await promisify(
      binStore.get(fid) as IDBRequest<StoredBinary | undefined>,
    )) as StoredBinary | undefined;
    if (!prev) {
      continue;
    }
    const nextRef = prev.refCount - 1;
    if (nextRef <= 0) {
      await promisify(binStore.delete(fid));
    } else {
      prev.refCount = nextRef;
      await promisify(binStore.put(prev));
    }
  }
  await promisify(docsStore.delete(id));
  // clear active if it was this one
  const activeId = (await promisify(
    metaStore.get(META_KEYS.ACTIVE) as IDBRequest<string | undefined>,
  )) as string | undefined;
  if (activeId === id) {
    await promisify(metaStore.delete(META_KEYS.ACTIVE));
  }
  // adjust usage
  const prevUsage =
    ((await promisify(
      metaStore.get(META_KEYS.USAGE) as IDBRequest<number | undefined>,
    )) as number | undefined) ?? 0;
  await promisify(
    metaStore.put(
      Math.max(0, prevUsage - (existing.sizeBytes ?? 0)),
      META_KEYS.USAGE,
    ),
  );
  await txDone(tx);
}

export async function getActiveDocId(): Promise<string | null> {
  const db = await openDb();
  const tx = db.transaction([STORES.META], "readonly");
  const v = (await promisify(
    tx.objectStore(STORES.META).get(META_KEYS.ACTIVE) as IDBRequest<
      string | undefined
    >,
  )) as string | undefined;
  await txDone(tx);
  return v ?? null;
}

export async function setActiveDocId(id: string | null): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORES.META], "readwrite");
  const store = tx.objectStore(STORES.META);
  if (id == null) {
    await promisify(store.delete(META_KEYS.ACTIVE));
  } else {
    await promisify(store.put(id, META_KEYS.ACTIVE));
  }
  await txDone(tx);
}

export async function getMigrationVersion(): Promise<number> {
  const db = await openDb();
  const tx = db.transaction([STORES.META], "readonly");
  const v = (await promisify(
    tx.objectStore(STORES.META).get(META_KEYS.MIGRATION_VERSION) as IDBRequest<
      number | undefined
    >,
  )) as number | undefined;
  await txDone(tx);
  return v ?? 0;
}

export async function setMigrationVersion(version: number): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORES.META], "readwrite");
  await promisify(
    tx.objectStore(STORES.META).put(version, META_KEYS.MIGRATION_VERSION),
  );
  await txDone(tx);
}

/**
 * Migration v5: stamp `sceneKey` on docs written before the field existed.
 * A bound doc then reads as dirty (its binding has no `lastSyncedSceneKey`)
 * and gets pushed once; the push still checks the remote version first.
 */
export async function backfillSceneKeys(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([STORES.DOCS], "readwrite");
  const store = tx.objectStore(STORES.DOCS);
  const all = (await promisify(
    store.getAll() as IDBRequest<StoredDoc[]>,
  )) as StoredDoc[];
  for (const d of all) {
    if (d.sceneKey === undefined) {
      d.sceneKey = computeSceneKey(d.elements, d.fileIds, d.name);
      await promisify(store.put(d));
    }
  }
  await txDone(tx);
}
